import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Flex, Grid, Section, IconButton } from "@radix-ui/themes";
import { SidebarIcon } from "lucide-react";
import {
  ExploreGroupByColumnType,
  ExploreColumnType,
  isExploreGroupByColumnType,
  isExploreColumnType,
} from "@/v2/entities/explore/columns/ExploreColum.model";
import {
  ExploreInteractionType,
  exploreInteractionTypeToLabel,
} from "@/v2/entities/explore/Explore.model";
import { ExploreGroupByPickerTabs } from "@/v2/features/explore/ExploreGroupByPicker";
import { useExploreState } from "@/v2/features/explore/useExploreState";
import { ExploreFilterMenu } from "@/v2/features/explore-filter-menu/ExploreFilterMenu";
import { mergeFilter } from "@/v2/entities/explore/Explore.filter";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import {
  buildFiltersFromFilter,
  parseFiltersToFilterRequest,
} from "@/v2/features/explore-filter-menu/explore-filter.utils";
import { useExploreAggInteractions } from "@/v2/entities/explore/Explore.api";
import { AggInteractionsList } from "@/v2/features/validation/AggInteractionsList";
import { HomePageCard } from "@/v2/features/home/HomePageCard";
import {
  ExploreOverTime,
  ExploreOverTimeTabs,
  useExploreOverTimeTimeGrouping,
} from "@/v2/features/explore-widgets/ExploreOverTime";
import { ExplorePieChart } from "@/v2/features/explore-widgets/ExplorePieChart";
import { Tabs } from "@/v2/components/tabs/Tabs";
import { HomePageHeaderActions } from "@/v2/features/home/HomePageHeaderActions";
import { QaScoreDelta } from "./QaScoreDelta";

interface TeamOrAgentTabsProps {
  value: string;
  onValueChange: (value: string) => void;
}

export function TeamOrAgentTabs({
  value,
  onValueChange,
}: TeamOrAgentTabsProps) {
  return (
    <Tabs
      variant="data"
      value={value}
      onValueChange={onValueChange}
      tabs={[
        { value: "team", label: "Team" },
        { value: "agentAssigned", label: "Agent" },
      ]}
    />
  );
}

const allowedGroupByOptions: ExploreGroupByColumnType[] = [
  "team",
  "agentAssigned",
];

export function EvaluationsPage() {
  const { timeGrouping, handleTimeGroupingChange } =
    useExploreOverTimeTimeGrouping();

  const [source, setSource] = useState<ExploreInteractionType>("tickets");
  const [teamOrAgent, setTeamOrAgent] = useState<ExploreColumnType>("team");
  const [showSidebar, setShowSidebar] = useState(false);

  const {
    state,
    handleFiltersChange,
    handleDateRangeChange,
    handleGroupByChange,
    handleGroupClick,
  } = useExploreState({
    allowedGroupByOptions: allowedGroupByOptions,
  });

  const { groupBy, dateRange, filters, parsedDateRange } = state;

  const filter = useMemo<Filter>(() => {
    let filter: Filter = {
      and: [
        { createdAt: { gte: parsedDateRange.dateFrom } },
        { createdAt: { lte: parsedDateRange.dateTo } },
      ],
    };

    if (Object.keys(filters).length > 0) {
      const filterToMerge = parseFiltersToFilterRequest(filters);
      filter = mergeFilter(filter, filterToMerge);
    }

    return filter;
  }, [parsedDateRange, filters]);

  const hiddenFilters = useMemo(() => {
    const filterToMerge: Filter = {
      and: [{ avgQaScore: { gte: 0 } }],
    };

    if (source === "conversations") {
      filterToMerge.and.push({ subject: { isnotnull: "true" } });
    }

    return mergeFilter(filter, filterToMerge);
  }, [filter, source]);

  const hiddenFiltersV2 = useMemo(() => {
    const filterToMerge: Filter = {
      and: [{ avgQaScore: { gte: 0 } }, { validationStatus: { eq: true } }],
    };

    if (source === "conversations") {
      filterToMerge.and.push({ subject: { isnotnull: "true" } });
    }

    return mergeFilter(filter, filterToMerge);
  }, [filter, source]);

  const exploreAggInteractionsQuery = useExploreAggInteractions({
    source,
    groupBy,
    filter: hiddenFilters,
    enabled: groupBy != null,
  });

  const handleTeamOrAgentChange = useCallback((value: string) => {
    if (!isExploreColumnType(value)) {
      return;
    }

    if (value === "team") {
      setTeamOrAgent("team");
    } else {
      setTeamOrAgent("agentAssigned");
    }
  }, []);

  const lastGroupBy = useRef<string | null>(null);
  useEffect(() => {
    if (lastGroupBy.current != null && groupBy == null) {
      setShowSidebar(false);
    }

    if (lastGroupBy.current == null && groupBy != null) {
      setShowSidebar(true);
    }
  }, [groupBy]);

  useEffect(() => {
    lastGroupBy.current = groupBy ?? null;
  }, [groupBy]);

  return (
    <Flex>
      <Flex
        className={`${
          showSidebar ? "w-[320px] border-r" : "w-0"
        } transition-all duration-200 max-h-[3084px] min-[1640px]:max-h-[1710px]`}
      >
        {groupBy != null && isExploreGroupByColumnType(groupBy) && (
          <AggInteractionsList
            groupBy={groupBy}
            loading={exploreAggInteractionsQuery.isLoading}
            interactions={exploreAggInteractionsQuery.data?.results ?? []}
            selectedInteractionId={""}
            onSelect={handleGroupClick}
          />
        )}
      </Flex>

      <Flex
        pl={`${showSidebar ? "0" : "6"}`}
        pr="6"
        flexGrow="1"
        direction="column"
      >
        <Flex
          align="center"
          gap="2"
          className={`${
            showSidebar ? "pl-[6px]" : "pl-[32px]"
          } transition-all px-[12px] py-[6px] border-b`}
        >
          <IconButton
            size="2"
            variant="soft"
            color="gray"
            onClick={() => setShowSidebar(!showSidebar)}
          >
            <SidebarIcon className="h-4 w-4" />
          </IconButton>

          <ExploreGroupByPickerTabs
            source={source}
            groupBy={groupBy}
            setGroupBy={handleGroupByChange}
            allowedGroupByOptions={allowedGroupByOptions}
          />
        </Flex>

        <Flex
          px="12px"
          py="8px"
          className="border-b"
          align="center"
          justify="between"
          gap="2"
        >
          <ExploreFilterMenu
            size="2"
            source={source}
            value={filters}
            onChange={handleFiltersChange}
            label={`Evaluated ${exploreInteractionTypeToLabel[source].plural}`}
            filter={filter}
            allowedFields={["team", "agentAssigned", "avgQaScoreRoundRange", "validationStatus"]}
          />

          <Flex gap="2">
            <HomePageHeaderActions
              source={source}
              dateRange={dateRange}
              setSource={setSource}
              setDateRange={handleDateRangeChange}
            />

            <ExploreOverTimeTabs
              value={timeGrouping}
              onValueChange={handleTimeGroupingChange}
            />
          </Flex>
        </Flex>

        <Section size="4" pt="0">
          <Flex p="6" direction="column" gap="3" width="100%">
            <Grid
              columns={{
                initial: "1",
                xl: "2",
              }}
              rows={{
                initial: "repeat(6, 480px)",
                xl: "repeat(3, 480px)",
              }}
              gap="3"
              width="100%"
            >
              <HomePageCard
                key={`avg-qa-score-over-time-${source}`}
                linkProps={{
                  text: `Avg. QA Score Over Time`,
                  source,
                  filters: buildFiltersFromFilter(hiddenFilters),
                  sorting: [],
                  groupBy: "avgQaScoreRoundRange",
                  dateRange,
                  parsedDateRange,
                }}
              >
                <Flex className="h-full">
                  <ExploreOverTime
                    source={source}
                    filter={hiddenFilters}
                    groupBy={timeGrouping}
                    metric="avgQaScore"
                    hideValues={["unknown"]}
                  />
                </Flex>
              </HomePageCard>

              <HomePageCard
                key={`avg-qa-score-breakdown-${source}`}
                linkProps={{
                  text: "Avg. QA Score Breakdown",
                  source,
                  groupBy: "avgQaScoreRoundRange",
                  filters: buildFiltersFromFilter(hiddenFilters),
                  sorting: [],
                  dateRange,
                  parsedDateRange,
                }}
              >
                <ExplorePieChart
                  source={source}
                  filter={hiddenFilters}
                  groupBy="avgQaScoreRoundRange"
                />
              </HomePageCard>

              <HomePageCard
                key={`avg-qa-delta-${source}`}
                linkProps={{
                  text: `Avg. QA Score Delta`,
                  source,
                  filters: buildFiltersFromFilter(hiddenFiltersV2),
                  sorting: [],
                  dateRange,
                  parsedDateRange,
                }}
                tooltip={
                  <div className="text-sm p-2">
                    Showing only validated interactions.
                  </div>
                }
              >
                <Flex className="h-full">
                  <QaScoreDelta
                    source={source}
                    filter={hiddenFiltersV2}
                    groupBy={timeGrouping}
                  />
                </Flex>
              </HomePageCard>

              <HomePageCard
                key={`avg-qa-score-split-by-${teamOrAgent}-over-time-${source}`}
                linkProps={{
                  text: `Avg. QA Score Over Time`,
                  source,
                  filters: buildFiltersFromFilter(hiddenFilters),
                  sorting: [],
                  dateRange,
                  parsedDateRange,
                }}
                actions={
                  <TeamOrAgentTabs
                    value={teamOrAgent}
                    onValueChange={handleTeamOrAgentChange}
                  />
                }
              >
                <Flex className="h-full">
                  <ExploreOverTime
                    variant="line"
                    source={source}
                    filter={hiddenFilters}
                    groupBy={timeGrouping}
                    splitBy={teamOrAgent}
                    metric="avgQaScore"
                    hideValues={["unknown"]}
                  />
                </Flex>
              </HomePageCard>

              <HomePageCard
                key={`avg-qa-score-split-by-score-title-over-time-${source}`}
                linkProps={{
                  text: `Avg. QA Score Per Category`,
                  source,
                  filters: buildFiltersFromFilter(hiddenFilters),
                  sorting: [],
                  dateRange,
                  parsedDateRange,
                }}
              >
                <Flex className="h-full">
                  <ExploreOverTime
                    variant="line"
                    source={source}
                    filter={hiddenFilters}
                    groupBy={timeGrouping}
                    splitBy={"scoreTitle"}
                    metric="avgQaScoreByTitle"
                    hideValues={["unknown"]}
                  />
                </Flex>
              </HomePageCard>

              <HomePageCard
                key={`avg-qa-score-split-by-score-content-over-time-${source}`}
                linkProps={{
                  text: `Avg. QA Score Per Question`,
                  source,
                  filters: buildFiltersFromFilter(hiddenFilters),
                  sorting: [],
                  dateRange,
                  parsedDateRange,
                }}
              >
                <Flex className="h-full">
                  <ExploreOverTime
                    variant="line"
                    source={source}
                    filter={hiddenFilters}
                    groupBy={timeGrouping}
                    splitBy={"scoreContent"}
                    metric="avgQaScoreByContent"
                    hideValues={["unknown"]}
                  />
                </Flex>
              </HomePageCard>
            </Grid>
          </Flex>
        </Section>
      </Flex>
    </Flex>
  );
}
